<script lang="ts">
  // Terms editor per INTERFACE-SPECIFICATION.md §6 (Detail / Edit Mode).
  //
  // terms_months on each product is a flat list of integers, e.g.
  // [36, 60, 84]. The server contract for staged edits carries the
  // FULL replacement list (term_edits: number[]) rather than a diff,
  // so the editor presents the current list as removable chips with
  // an add input below. The staged list is the chip set as the
  // operator currently sees it; staging clears when the chip set
  // matches the baseline (the operator removed an addition or re-added
  // a removal).
  //
  // Schema rules enforced inline (schema/calculator-source-of-truth.schema.json):
  // - Each term is a positive integer (>= 1).
  // - At least one term must remain after the edit.
  // - No duplicates.
  //
  // The server-side validator catches additional structural failures
  // (e.g. monotonicity or methodology cross-references) at submit time.

  import {
    stageTermEdits,
    unstageTermEdits,
    stagedEditsState
  } from '../lib/staged-edits.svelte';
  import { navigate } from '../lib/router.svelte';
  import type { ProductCatalogueFile, ProposeResponse } from '../lib/types';
  import SubmitForReview from './SubmitForReview.svelte';
  import ValueTypeLockBanner from './ValueTypeLockBanner.svelte';

  interface Props {
    catalogue: ProductCatalogueFile;
  }

  const { catalogue }: Props = $props();

  // Baseline is the catalogue's current terms_months, sorted ascending.
  // Captured by-value so a catalogue refresh mid-edit does not move the
  // baseline under the operator's feet.
  const baseline = $derived([...catalogue.terms_months].sort((a, b) => a - b));

  // The staged list (sorted, unique). Falls back to baseline when no
  // edit is staged so the chip row always reflects the current intent.
  const stagedList = $derived(stagedEditsState.term_edits?.staged ?? baseline);

  // Lock when a different value type holds the in-flight draft. The
  // banner replaces the editor inputs so the operator sees a clean
  // Discard affordance with explanatory copy.
  const locked = $derived(
    stagedEditsState.valueType !== null && stagedEditsState.valueType !== 'terms'
  );

  const addBuffer = $state<{ value: string; error: string | null }>({
    value: '',
    error: null
  });

  let submitOpen = $state<boolean>(false);

  function openSubmit() { submitOpen = true; }
  function closeSubmit() { submitOpen = false; }
  function onProposalCreated(response: ProposeResponse) {
    submitOpen = false;
    navigate(`/changes/pending-review/${response.pr_number}`);
  }

  function parsePositiveInteger(raw: string): number | null {
    const trimmed = raw.trim();
    if (!/^[0-9]+$/.test(trimmed)) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) && Number.isInteger(n) && n >= 1 ? n : null;
  }

  function removeTerm(term: number) {
    if (locked) return;
    const remaining = stagedList.filter((t) => t !== term);
    if (remaining.length === 0) {
      addBuffer.error = 'Cannot remove the last term — at least one must remain.';
      return;
    }
    addBuffer.error = null;
    stageTermEdits(baseline, remaining);
  }

  function addTerm() {
    if (locked) return;
    const parsed = parsePositiveInteger(addBuffer.value);
    if (parsed === null) {
      addBuffer.error = 'Enter a positive whole number of months.';
      return;
    }
    if (stagedList.includes(parsed)) {
      addBuffer.error = `${parsed} months is already in the list.`;
      return;
    }
    const next = [...stagedList, parsed].sort((a, b) => a - b);
    addBuffer.error = null;
    addBuffer.value = '';
    stageTermEdits(baseline, next);
  }

  function handleAddKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      addTerm();
    }
  }

  function revertAll() {
    addBuffer.value = '';
    addBuffer.error = null;
    unstageTermEdits();
  }

  // Diff against baseline for the change-line summary. Added is in
  // staged but not baseline; removed is in baseline but not staged.
  const addedTerms = $derived(stagedList.filter((t) => !baseline.includes(t)));
  const removedTerms = $derived(baseline.filter((t) => !stagedList.includes(t)));
  const hasStagedChange = $derived(addedTerms.length > 0 || removedTerms.length > 0);
</script>

<section class="terms-editor">
  {#if locked && stagedEditsState.valueType}
    <ValueTypeLockBanner
      currentValueType={stagedEditsState.valueType}
      attemptedValueType="terms"
    />
  {/if}

  <div class="terms-editor__chips" class:is-locked={locked}>
    {#each stagedList as term (term)}
      {@const added = addedTerms.includes(term)}
      <span class="term-chip" class:is-added={added}>
        <span class="term-chip__value">{term} months</span>
        <button
          class="term-chip__remove"
          type="button"
          aria-label={`Remove ${term} months`}
          disabled={locked}
          onclick={() => removeTerm(term)}
        >×</button>
      </span>
    {/each}
    {#each removedTerms as term (term)}
      <span class="term-chip is-removed">
        <span class="term-chip__value"><s>{term} months</s></span>
        <button
          class="term-chip__remove"
          type="button"
          aria-label={`Restore ${term} months`}
          disabled={locked}
          onclick={() => { addBuffer.value = String(term); addTerm(); }}
        >+</button>
      </span>
    {/each}
  </div>

  <div class="terms-editor__add">
    <label>
      <span class="muted">Add term (months)</span>
      <input
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        placeholder="e.g. 48"
        disabled={locked}
        bind:value={addBuffer.value}
        onkeydown={handleAddKeyDown}
      />
    </label>
    <button class="btn btn-secondary" type="button" disabled={locked} onclick={addTerm}>Add</button>
  </div>
  {#if addBuffer.error}
    <p class="field-error">{addBuffer.error}</p>
  {/if}

  {#if hasStagedChange && !locked}
    <div class="terms-editor__summary">
      {#if addedTerms.length > 0}
        <span class="summary-add">Added: {addedTerms.join(', ')}</span>
      {/if}
      {#if removedTerms.length > 0}
        <span class="summary-remove">Removed: {removedTerms.join(', ')}</span>
      {/if}
    </div>
    <div class="terms-editor__actions">
      <button class="btn-link" type="button" onclick={revertAll}>Revert all</button>
      <button class="btn btn-primary" type="button" onclick={openSubmit}>Submit for review →</button>
    </div>
  {/if}
</section>

{#if submitOpen}
  <SubmitForReview
    {catalogue}
    valueType="terms"
    onClose={closeSubmit}
    onSubmitted={onProposalCreated}
  />
{/if}

<style>
  .terms-editor {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .terms-editor__chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  .terms-editor__chips.is-locked {
    opacity: 0.55;
  }
  .term-chip {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1) var(--space-2);
    border: 1px solid var(--surface-line);
    border-radius: var(--radius-md);
    background: var(--surface-card);
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-size: var(--type-sm, 0.875rem);
  }
  .term-chip.is-added {
    border-color: var(--accent-pass);
    background: var(--accent-pass-bg);
  }
  .term-chip.is-removed {
    border-color: var(--accent-fail);
    background: var(--accent-fail-bg);
    color: var(--surface-muted);
  }
  .term-chip__remove {
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 1.125rem;
    line-height: 1;
    padding: 0 var(--space-1);
    color: var(--surface-muted);
  }
  .term-chip__remove:hover:not(:disabled) {
    color: var(--surface-fg);
  }
  .term-chip__remove:disabled {
    cursor: not-allowed;
  }
  .terms-editor__add {
    display: flex;
    align-items: flex-end;
    gap: var(--space-3);
  }
  .terms-editor__add label {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .terms-editor__add input {
    width: 8rem;
    padding: var(--space-1) var(--space-2);
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
  }
  .field-error {
    margin: 0;
    color: var(--accent-fail);
    font-size: var(--type-xs, 0.75rem);
  }
  .terms-editor__summary {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
    font-size: var(--type-sm, 0.875rem);
  }
  .summary-add { color: var(--accent-pass); }
  .summary-remove { color: var(--accent-fail); }
  .terms-editor__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: var(--space-2);
  }
</style>
